import React, { useState } from "react";

const PROVIDERS = [
  { key: "openai", label: "DALL-E (OpenAI)", endpoint: "https://api.openai.com/v1/images/generations" },
  { key: "stability", label: "Stability AI", endpoint: "https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image" },
];

const SETTINGS_KEY = "striply_ai_settings";

const loadSettings = () => {
  try {
    const saved = localStorage.getItem(SETTINGS_KEY);
    return saved ? JSON.parse(saved) : {};
  } catch { return {}; }
};

const saveSettings = (settings) => {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn("Failed to save AI settings:", e);
  }
};

const AIGenerateModal = ({ onClose, onGenerated }) => {
  const [settings] = useState(loadSettings);
  const [provider, setProvider] = useState(settings.provider || "openai");
  const [apiKey, setApiKey] = useState(settings.apiKey || "");
  const [prompt, setPrompt] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleGenerate = async () => {
    if (!apiKey.trim()) {
      setError("Please enter your API key");
      return;
    }
    if (!prompt.trim()) {
      setError("Please enter a prompt");
      return;
    }

    // Save settings
    saveSettings({ provider, apiKey });

    setIsLoading(true);
    setError(null);

    try {
      let imageUrl;

      if (provider === "openai") {
        const response = await fetch("https://api.openai.com/v1/images/generations", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model: "dall-e-3",
            prompt: prompt,
            n: 1,
            size: "1024x1024",
            response_format: "b64_json",
          }),
        });

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error?.message || `OpenAI API error: ${response.status}`);
        }

        const data = await response.json();
        imageUrl = `data:image/png;base64,${data.data[0].b64_json}`;
      } else if (provider === "stability") {
        const response = await fetch(
          "https://api.stability.ai/v1/generation/stable-diffusion-xl-1024-v1-0/text-to-image",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${apiKey}`,
              Accept: "application/json",
            },
            body: JSON.stringify({
              text_prompts: [{ text: prompt, weight: 1 }],
              cfg_scale: 7,
              height: 1024,
              width: 1024,
              samples: 1,
              steps: 30,
            }),
          }
        );

        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.message || `Stability API error: ${response.status}`);
        }

        const data = await response.json();
        imageUrl = `data:image/png;base64,${data.artifacts[0].base64}`;
      }

      onGenerated(imageUrl);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1000,
        background: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 8,
          padding: 24,
          width: 440,
          maxWidth: "90vw",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 style={{ margin: "0 0 16px", fontSize: 18 }}>AI Generate Image</h3>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Provider
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              style={{ display: "block", width: "100%", padding: "6px 8px", marginTop: 4, fontSize: 13, border: "1px solid #ccc", borderRadius: 4 }}
            >
              {PROVIDERS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </label>

          <label style={{ fontSize: 13, fontWeight: 600 }}>
            API Key
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Enter your API key..."
              style={{ display: "block", width: "100%", padding: "6px 8px", marginTop: 4, fontSize: 13, border: "1px solid #ccc", borderRadius: 4, boxSizing: "border-box" }}
            />
          </label>

          <label style={{ fontSize: 13, fontWeight: 600 }}>
            Prompt
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe the image you want to generate..."
              rows={3}
              style={{ display: "block", width: "100%", padding: "6px 8px", marginTop: 4, fontSize: 13, border: "1px solid #ccc", borderRadius: 4, resize: "vertical", boxSizing: "border-box" }}
            />
          </label>

          {error && (
            <div style={{ color: "#b42318", fontSize: 12, padding: "8px 12px", background: "#fef2f2", borderRadius: 4 }}>
              {error}
            </div>
          )}

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{ padding: "8px 16px", border: "1px solid #ccc", background: "#fff", borderRadius: 4, cursor: "pointer", fontSize: 13 }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleGenerate}
              disabled={isLoading}
              style={{
                padding: "8px 16px",
                border: "none",
                background: isLoading ? "#94a3b8" : "#3b82f6",
                color: "#fff",
                borderRadius: 4,
                cursor: isLoading ? "default" : "pointer",
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {isLoading ? "Generating..." : "Generate"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12, fontSize: 11, color: "#64748b" }}>
          Your API key is stored locally and never sent to our servers.
        </div>
      </div>
    </div>
  );
};

export default AIGenerateModal;
